import { z } from 'zod';
import { FRENCH_SUBDIVISIONS } from '../data/french-subdivisions.js';
// A second-level geographic subdivision, independent of business departments and regions.
export const geographicSubdivisionFields = {
    subdivisionCode: z.string().trim().max(20).nullish(),
    subdivisionName: z.string().trim().max(120).nullish(),
};
export const geographicSubdivisionSchema = z.object({
    countryCode: z.string().nullish(),
    ...geographicSubdivisionFields,
});
export function subdivisionIssue(value) {
    const code = value.subdivisionCode;
    const name = value.subdivisionName;
    if (!code && !name)
        return null;
    if (!code || !name)
        return 'subdivision.incomplete';
    if (!value.countryCode)
        return 'subdivision.countryRequired';
    if (value.countryCode !== 'FR')
        return null;
    const entry = FRENCH_SUBDIVISIONS.find((row) => row.code === code);
    return entry?.name === name ? null : 'subdivision.invalidPair';
}
export function validateSubdivision(value, ctx) {
    const issue = subdivisionIssue(value);
    if (issue)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['subdivisionCode'], message: issue });
}
/** Country changes clear inherited geography; explicit replacement values are validated. */
export function subdivisionPatch(input, existing) {
    const changedCountry = existing !== undefined &&
        input.countryCode !== undefined &&
        input.countryCode !== existing.countryCode;
    const touched = changedCountry || input.subdivisionCode !== undefined || input.subdivisionName !== undefined;
    if (!touched)
        return {};
    const value = geographicSubdivisionSchema.superRefine(validateSubdivision).parse({
        countryCode: input.countryCode ?? existing?.countryCode,
        subdivisionCode: input.subdivisionCode === undefined
            ? changedCountry
                ? null
                : existing?.subdivisionCode
            : input.subdivisionCode,
        subdivisionName: input.subdivisionName === undefined
            ? changedCountry
                ? null
                : existing?.subdivisionName
            : input.subdivisionName,
    });
    // Prevent a full-form API update from silently carrying French geography to another country.
    if (changedCountry &&
        existing.countryCode === 'FR' &&
        value.subdivisionCode &&
        value.subdivisionCode === existing.subdivisionCode &&
        value.subdivisionName === existing.subdivisionName) {
        throw new z.ZodError([
            {
                code: z.ZodIssueCode.custom,
                path: ['subdivisionCode'],
                message: 'subdivision.countryChanged',
            },
        ]);
    }
    return {
        subdivisionCode: value.subdivisionCode ?? null,
        subdivisionName: value.subdivisionName ?? null,
    };
}
export function subdivisionLabel(value) {
    return [value.subdivisionCode, value.subdivisionName].filter(Boolean).join(' – ');
}
//# sourceMappingURL=geographic-subdivision.js.map